import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { PERMISSIONS } from '../lib/permissions';
import { LazyRoute } from './LazyRoute';
import { RequireAuth } from './RequireAuth';
import { RequirePasswordCurrent } from './RequirePasswordCurrent';
import { RequirePermission } from './RequirePermission';
import { ROUTES } from './routes';

/**
 * Route-level code splitting, and specifically the split Story G requires:
 * **`/login` and `/change-password` are their own chunks and must never pull
 * in `AppShell`, `nav-items`, or a module page.** `AppShell` is therefore
 * lazy too — a static import here would put the whole authenticated shell in
 * the entry chunk and hand an unauthenticated phone on plant wifi the entire
 * app before it can type an email address.
 *
 * If you add a route: import it lazily, and do not import an authenticated
 * module from an unauthenticated one.
 */
const AppShell = lazy(() =>
  import('./layout/AppShell').then((module) => ({ default: module.AppShell })),
);
const LoginPage = lazy(() =>
  import('../features/auth/LoginPage').then((module) => ({ default: module.LoginPage })),
);
const ChangePasswordPage = lazy(() =>
  import('../features/auth/ChangePasswordPage').then((module) => ({
    default: module.ChangePasswordPage,
  })),
);
const DashboardPage = lazy(() =>
  import('../features/dashboard/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  })),
);
const UsersPage = lazy(() =>
  import('../features/users/UsersPage').then((module) => ({ default: module.UsersPage })),
);
const RolesPage = lazy(() =>
  import('../features/roles/RolesPage').then((module) => ({ default: module.RolesPage })),
);

// --- feature 002. Each is its own chunk: a Store Keeper who only ever opens
// Goods Receipts and Stock should never download the vendor or material forms.
const FinishedGoodsPage = lazy(() =>
  import('../features/finished-goods/FinishedGoodsPage').then((module) => ({
    default: module.FinishedGoodsPage,
  })),
);
const RecipesPage = lazy(() =>
  import('../features/recipes/RecipesPage').then((module) => ({
    default: module.RecipesPage,
  })),
);

// --- feature 004. Its own chunk: a Store Keeper who never raises a purchase
// order should not download the register or the document drawer.
const PurchaseOrdersPage = lazy(() =>
  import('../features/purchase-orders/PurchaseOrdersPage').then((module) => ({
    default: module.PurchaseOrdersPage,
  })),
);
/*
 * The print view is its **own chunk**, not part of the purchase-orders page:
 * it is a standalone route reached rarely, and bundling an A4 layout plus its
 * print stylesheet into the register's chunk would make every visit to the
 * register pay for it. 012's print route is split for the same reason.
 */
const PurchaseOrderPrintView = lazy(() =>
  import('../features/purchase-orders/PurchaseOrderPrintView').then((module) => ({
    default: module.PurchaseOrderPrintView,
  })),
);
const RawMaterialsPage = lazy(() =>
  import('../features/raw-materials/RawMaterialsPage').then((module) => ({
    default: module.RawMaterialsPage,
  })),
);
const VendorsPage = lazy(() =>
  import('../features/vendors/VendorsPage').then((module) => ({ default: module.VendorsPage })),
);
const TransportersPage = lazy(() =>
  import('../features/transporters/TransportersPage').then((module) => ({
    default: module.TransportersPage,
  })),
);
const GoodsReceiptsPage = lazy(() =>
  import('../features/goods-receipts/GoodsReceiptsPage').then((module) => ({
    default: module.GoodsReceiptsPage,
  })),
);
/*
 * Entry and the posted document are drawers over the list now (ADR-0021), so
 * they ship inside the `GoodsReceiptsPage` chunk. What is left on the two old
 * paths is a redirect apiece — they are live URLs and must not 404.
 */
const GoodsReceiptNewRedirect = lazy(() =>
  import('../features/goods-receipts/GoodsReceiptRedirects').then((module) => ({
    default: module.GoodsReceiptNewRedirect,
  })),
);
const GoodsReceiptRecordRedirect = lazy(() =>
  import('../features/goods-receipts/GoodsReceiptRedirects').then((module) => ({
    default: module.GoodsReceiptRecordRedirect,
  })),
);
/*
 * Feature 012's print view is its **own chunk**, not part of the receipts
 * page: it is a standalone route reached rarely, and bundling an A4 layout
 * plus its print stylesheet into the list's chunk would make every visit to
 * the list pay for it.
 */
const GoodsReceiptPrintView = lazy(() =>
  import('../features/goods-receipts/GoodsReceiptPrintView').then((module) => ({
    default: module.GoodsReceiptPrintView,
  })),
);
const PurchaseReturnsPage = lazy(() =>
  import('../features/purchase-returns/PurchaseReturnsPage').then((module) => ({
    default: module.PurchaseReturnsPage,
  })),
);
const StockPage = lazy(() =>
  import('../features/stock/StockPage').then((module) => ({ default: module.StockPage })),
);
const PlantProfilePage = lazy(() =>
  import('../features/plant-profile/PlantProfilePage').then((module) => ({
    default: module.PlantProfilePage,
  })),
);

/**
 * Feature 010. Its own chunk, and a large one — the group editor carries the
 * component composer. A Store Keeper holds neither `tax_config` code and
 * should never download any of it.
 */
const TaxConfigurationPage = lazy(() =>
  import('../features/taxes/TaxConfigurationPage').then((module) => ({
    default: module.TaxConfigurationPage,
  })),
);

/**
 * Feature 016, amended by 021: the plans page is its own chunk (the composer carries the
 * pour-figure strip, the capacity verdict and the requirement list), and so is the mould setting
 * that replaced 016's Moulds master. An Accountant holding none of the codes downloads neither.
 */
const PlantMouldSettingPage = lazy(() =>
  import('../features/plant-mould-setting/PlantMouldSettingPage').then((module) => ({
    default: module.PlantMouldSettingPage,
  })),
);
const ManufacturingPlansPage = lazy(() =>
  import('../features/manufacturing-plans/ManufacturingPlansPage').then((module) => ({
    default: module.ManufacturingPlansPage,
  })),
);
/** Feature 005 (005/design.md §6.1): the production run register, its own chunk. */
const ProductionRunsPage = lazy(() =>
  import('../features/production-runs/ProductionRunsPage').then((module) => ({
    default: module.ProductionRunsPage,
  })),
);

const PaymentsPage = lazy(() =>
  import('../features/payments/PaymentsPage').then((module) => ({
    default: module.PaymentsPage,
  })),
);
const AccountHeadsPage = lazy(() =>
  import('../features/account-heads/AccountHeadsPage').then((module) => ({
    default: module.AccountHeadsPage,
  })),
);
const PaymentMethodsPage = lazy(() =>
  import('../features/payment-methods/PaymentMethodsPage').then((module) => ({
    default: module.PaymentMethodsPage,
  })),
);

/**
 * Feature 017 — TCS Register. Finance workspace only; guarded on
 * `goods_receipt.read` — no new permission code. Its own chunk: an
 * Accountant who never posts receipts should not download the receipt
 * entry form, and conversely a Store Keeper who never views the TCS
 * register should not download this page.
 */
const TcsRegisterPage = lazy(() =>
  import('../features/tcs-charges/TcsRegisterPage').then((module) => ({
    default: module.TcsRegisterPage,
  })),
);

/**
 * Feature 025 — delivery challans. Its own chunk: an Accountant who only ever
 * reads the open-challan register should not download the gate's entry
 * composer, and a Store Keeper who never opens the register still pays for
 * neither until they navigate here.
 */
const DeliveryChallansPage = lazy(() =>
  import('../features/delivery-challans/DeliveryChallansPage').then((module) => ({
    default: module.DeliveryChallansPage,
  })),
);
/*
 * The print view is its **own chunk**, not part of the challan page, for the
 * reason 012's and 004's print routes are split: an A4 layout plus its print
 * stylesheet should not be downloaded by every visit to the list.
 */
const DeliveryChallanPrintView = lazy(() =>
  import('../features/delivery-challans/DeliveryChallanPrintView').then((module) => ({
    default: module.DeliveryChallanPrintView,
  })),
);
/* D-20's verification sheet — its own chunk too, for the same reason. */
const ChallanStatementPrintView = lazy(() =>
  import('../features/delivery-challans/ChallanStatementPrintView').then((module) => ({
    default: module.ChallanStatementPrintView,
  })),
);

/**
 * Feature 026 — vendor bills. Its own chunk: a Store Keeper holds **no**
 * `vendor_bill.*` code at all (AS-2) and must never download the bill's
 * composer, which carries the settlement step, the priced-line editor and the
 * money block; an Accountant who never opens the challan register still pays
 * for neither until they navigate here.
 */
const VendorBillsPage = lazy(() =>
  import('../features/vendor-bills/VendorBillsPage').then((module) => ({
    default: module.VendorBillsPage,
  })),
);
/* The print view is its **own chunk**, not part of the bills page, for the
   reason 012's, 004's and 025's print routes are split: an A4 layout plus its
   print stylesheet should not be downloaded by every visit to the list. */
const VendorBillPrintView = lazy(() =>
  import('../features/vendor-bills/VendorBillPrintView').then((module) => ({
    default: module.VendorBillPrintView,
  })),
);

const NotFoundPage = lazy(() =>
  import('../features/misc/NotFoundPage').then((module) => ({ default: module.NotFoundPage })),
);

/**
 * Guard order is deliberate and matches the backend's pipeline:
 *   RequireAuth → RequirePasswordCurrent → RequirePermission
 *
 * Authentication before the forced-password check (there is nothing to force
 * on an anonymous visitor), and the forced-password check above the shell so
 * no nav, no module page and no list request renders behind the redirect.
 * The per-screen permission guard sits inside, where the screen is.
 */
export const router = createBrowserRouter([
  {
    path: ROUTES.login,
    element: (
      <LazyRoute>
        <LoginPage />
      </LazyRoute>
    ),
  },
  {
    // Authenticated but outside the shell: a blocking surface, no nav chrome.
    path: ROUTES.changePassword,
    element: (
      <RequireAuth>
        <LazyRoute>
          <ChangePasswordPage />
        </LazyRoute>
      </RequireAuth>
    ),
  },
  {
    /*
     * **Outside the app shell, and that is the whole point** (012/design.md
     * §7.2, AC-9).
     *
     * A printed receipt must have no nav, no bottom bar and no drawer in it —
     * **absent from the DOM, not hidden by a print rule**. Nesting this under
     * `AppShell` like every other authenticated route would put the sidebar
     * and the mobile tab bar in the document and leave `@media print` fighting
     * to suppress them, which is exactly the fragility this route exists to
     * avoid. So it sits beside `changePassword`, the other authenticated
     * surface with no chrome.
     *
     * It is also declared **before the shell route — belt and braces, not the
     * mechanism**. Rank already protects it: `/goods-receipts/:id/print` scores
     * 28 against 16 for `/goods-receipts/:id/*` and 12 for `/goods-receipts/*`,
     * rank is resolved before declaration order, and the shell's *leaf*
     * `/goods-receipts/:id` redirect cannot match a three-segment path at all.
     * Order decides in exactly one arrangement: that `:id` as a *layout* route
     * with a nested `{ path: 'print' }` child — identical full path, identical
     * score, so the tie falls to declaration order. **Do not move this below
     * the shell.**
     *
     * `RequirePasswordCurrent` is deliberately **not** applied — it is absent
     * on `changePassword` for the same reason, and a receipt is read-only.
     * There is no `goods_receipt.print` permission: printing renders exactly
     * what `goods_receipt.read` authorises, so that is the guard.
     */
    path: ROUTES.goodsReceiptPrint,
    element: (
      <RequireAuth>
        <RequirePermission anyOf={[PERMISSIONS.GOODS_RECEIPT_READ]}>
          <LazyRoute>
            <GoodsReceiptPrintView />
          </LazyRoute>
        </RequirePermission>
      </RequireAuth>
    ),
  },
  {
    /*
     * **Feature 004's print route — outside the app shell, and that is the
     * whole point** (004/design.md §6.3 screen 5, AC-B4). The reasoning is
     * 012's, one route above, and this is the second instance of it: nav,
     * buttons and drawers must be **absent from the DOM**, not hidden by a
     * print rule, so the route cannot be nested under `AppShell`. It sits
     * beside `changePassword` and `goodsReceiptPrint`, the other authenticated
     * surfaces with no chrome.
     *
     * **Declared before the shell route — belt and braces, not the
     * mechanism** (design §6.2 states the ordering as a hard constraint).
     * React Router 7 already protects this path by rank:
     * `/purchase-orders/:id/print` scores 28 against 16 for
     * `/purchase-orders/:id/*` and 12 for `/purchase-orders/*`, rank is resolved
     * before declaration order, and a *leaf* `/purchase-orders/:id` cannot
     * match a three-segment path at all. Order decides in exactly one
     * arrangement: a shell *layout* route `/purchase-orders/:id` carrying a
     * nested `{ path: 'print' }` child — identical full path, identical score,
     * so the tie falls to declaration order and we win it from here. **Do not
     * move this below the shell.**
     *
     * `RequirePasswordCurrent` is deliberately **not** applied, as on the two
     * routes above. There is no `purchase_order.print` permission either:
     * printing renders exactly what `purchase_order.read` authorises, so that
     * is the guard, and there is no print endpoint to guard separately.
     */
    path: ROUTES.purchaseOrderPrint,
    element: (
      <RequireAuth>
        <RequirePermission anyOf={[PERMISSIONS.PURCHASE_ORDER_READ]}>
          <LazyRoute>
            <PurchaseOrderPrintView />
          </LazyRoute>
        </RequirePermission>
      </RequireAuth>
    ),
  },
  {
    /*
     * **D-20's vendor statement print page — and it MUST stay above the
     * challan print route below it** (025/design.md §6.1, S12, AC-N8).
     *
     * This is a **literal** path where the next route is **parameterised**:
     * `/delivery-challans/statement/print` against
     * `/delivery-challans/:id/print`. React Router 7 scores a static segment
     * above a dynamic one, so rank already settles this — but the two paths
     * have the same segment count and the same shape, which is exactly the
     * arrangement where a later refactor (a layout route at
     * `/delivery-challans/:id` with a `{ path: 'print' }` child) produces an
     * identical score and the tie falls to declaration order. Get it wrong
     * and `statement` binds as `:id`: the page fetches a challan by the id
     * `"statement"`, answers *"must be a valid id"*, and the statement looks
     * absent while being perfectly well mounted — the frontend twin of the
     * backend hazard §4.5b records. **Do not move this below the route that
     * follows.**
     *
     * Guarded on `delivery_challan.read` (AC-N9) — there is deliberately no
     * `delivery_challan.print` code, and the statement discloses nothing the
     * register does not already disclose to the same reader (D-20 constraint
     * 9). `RequirePasswordCurrent` is not applied, as on every print route
     * above.
     */
    path: ROUTES.deliveryChallanStatementPrint,
    element: (
      <RequireAuth>
        <RequirePermission anyOf={[PERMISSIONS.DELIVERY_CHALLAN_READ]}>
          <LazyRoute>
            <ChallanStatementPrintView />
          </LazyRoute>
        </RequirePermission>
      </RequireAuth>
    ),
  },
  {
    /*
     * **Feature 025's print route — outside the app shell, and that is the
     * whole point** (025/design.md §4.8, §6.1, AC-B2). The third instance of
     * 012's reasoning: nav, buttons and drawers must be **absent from the
     * DOM**, not hidden by a print rule, so this cannot nest under `AppShell`.
     *
     * Guarded on `delivery_challan.read` — there is deliberately no
     * `delivery_challan.print` code and no `GET /:id/print` endpoint: the page
     * renders from the `.read`-gated detail endpoint plus the plant profile,
     * so `.read` is both the only data source's guard and this route's
     * (AC-B5). `RequirePasswordCurrent` is deliberately not applied, as on the
     * two print routes above.
     */
    path: ROUTES.deliveryChallanPrint,
    element: (
      <RequireAuth>
        <RequirePermission anyOf={[PERMISSIONS.DELIVERY_CHALLAN_READ]}>
          <LazyRoute>
            <DeliveryChallanPrintView />
          </LazyRoute>
        </RequirePermission>
      </RequireAuth>
    ),
  },
  {
    /*
     * **Feature 026's print route — outside the app shell, and that is the
     * whole point** (026/design.md §6.1, AC-P4). The fifth instance of 012's
     * reasoning: nav, buttons and drawers must be **absent from the DOM**, not
     * hidden by a print rule, so this cannot nest under `AppShell`.
     *
     * Guarded on `vendor_bill.read` — there is deliberately no
     * `vendor_bill.print` code and no print endpoint: the page renders from the
     * `.read`-gated detail plus the plant profile, so `.read` is both the data
     * source's guard and this route's. `RequirePasswordCurrent` is not applied,
     * as on every print route above.
     *
     * **Declared above the shell** — belt and braces; see `ROUTES.vendorBillPrint`.
     */
    path: ROUTES.vendorBillPrint,
    element: (
      <RequireAuth>
        <RequirePermission anyOf={[PERMISSIONS.VENDOR_BILL_READ]}>
          <LazyRoute>
            <VendorBillPrintView />
          </LazyRoute>
        </RequirePermission>
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.dashboard,
    element: (
      <RequireAuth>
        <RequirePasswordCurrent>
          <LazyRoute>
            <AppShell />
          </LazyRoute>
        </RequirePasswordCurrent>
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <LazyRoute>
            <DashboardPage />
          </LazyRoute>
        ),
      },
      {
        path: ROUTES.users,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.USER_MANAGE]}>
            <LazyRoute>
              <UsersPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.roles,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.ROLE_MANAGE]}>
            <LazyRoute>
              <RolesPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },

      /*
       * Feature 002 (002/design.md §6.2).
       *
       * Every guard asks for the **read** code, because read and manage are
       * separate codes in this feature (§2.5) — a screen guard that asked for
       * `raw_material.manage` would refuse the Auditor the list they are
       * entitled to see. The one exception is the receipt entry page, which
       * *is* the create action and therefore guards on
       * `goods_receipt.create`.
       */
      {
        path: ROUTES.rawMaterials,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.RAW_MATERIAL_READ]}>
            <LazyRoute>
              <RawMaterialsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
       * Feature 003. Both guard on the **read** code, which is what produces the visible
       * asymmetry the brief asks for: a Sales Executive holds `finished_good.read` and not
       * `recipe.read`, so they reach Finished Goods and get a 403 on Recipes (brief D-6).
       */
      {
        path: ROUTES.finishedGoods,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.FINISHED_GOOD_READ]}>
            <LazyRoute>
              <FinishedGoodsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.recipes,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.RECIPE_READ]}>
            <LazyRoute>
              <RecipesPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
        --- feature 004 (004/design.md §6.2). One route for the module; the
        list, the entry drawer, the document, a draft edit, an amendment and
        the revision surfaces are all query parameters over it (ADR-0021).

        The print route is **outside this shell** and is declared above it,
        beside the receipt's — see the comment there for the ordering
        constraint it satisfies.
      */
      {
        path: ROUTES.purchaseOrders,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.PURCHASE_ORDER_READ]}>
            <LazyRoute>
              <PurchaseOrdersPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.vendors,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.VENDOR_READ]}>
            <LazyRoute>
              <VendorsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.transporters,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.TRANSPORTER_READ]}>
            <LazyRoute>
              <TransportersPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.goodsReceipts,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.GOODS_RECEIPT_READ]}>
            <LazyRoute>
              <GoodsReceiptsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        // Declared before `:id` so the route table reads in the same order
        // React Router ranks it: a static segment beats a dynamic one, so
        // `/goods-receipts/new` can never be looked up as a receipt id.
        //
        // Still guarded: a redirect must never become a way around the
        // permission the page it replaced required.
        path: ROUTES.goodsReceiptNew,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.GOODS_RECEIPT_CREATE]}>
            <LazyRoute>
              <GoodsReceiptNewRedirect />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.goodsReceiptDetail,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.GOODS_RECEIPT_READ]}>
            <LazyRoute>
              <GoodsReceiptRecordRedirect />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.purchaseReturns,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.PURCHASE_RETURN_READ]}>
            <LazyRoute>
              <PurchaseReturnsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
       * Feature 025 (025/design.md §6.1). Guarded on the **read** code: the
       * list, the register, the document and the print page all require
       * `delivery_challan.read`, and the page renders no New / Edit / Void
       * control without the matching independent grant. The backend answers
       * 403 regardless (AC-M1).
       *
       * **No nav item points here** — the manager's ruling of 2026-10-21. The
       * route is reached from the tab strip on Goods Receipts, from the vendor
       * drawer, from the stock ledger and from a deep link.
       */
      {
        path: ROUTES.deliveryChallans,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.DELIVERY_CHALLAN_READ]}>
            <LazyRoute>
              <DeliveryChallansPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
       * Feature 026 (026/design.md §6.1). Guarded on the **read** code: the
       * list, the document, the print page and the attachments all require
       * `vendor_bill.read`, and the page renders no New / Correct / Void
       * control without the matching independent grant. The backend answers
       * 403 regardless (AC-M1).
       *
       * **No nav item points here** — the manager's 2026-10-21 ruling stands
       * (DT-026-18). The route is reached from the inbound tab strip, from the
       * challan detail, from the stock ledger, from a payment row and from a
       * deep link.
       */
      {
        path: ROUTES.vendorBills,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.VENDOR_BILL_READ]}>
            <LazyRoute>
              <VendorBillsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.stock,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.STOCK_READ]}>
            <LazyRoute>
              <StockPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.plantProfile,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.PLANT_PROFILE_READ]}>
            <LazyRoute>
              <PlantProfilePage />
            </LazyRoute>
          </RequirePermission>
        ),
      },

      /*
       * Feature 010 (010/design.md §7.1). Guarded on the **read** code, like
       * every feature-002 screen: `tax_config.manage` does not imply
       * `tax_config.read`, and an Accountant who may see the slabs but not
       * publish a version is entitled to the list. The page itself renders no
       * Create/Edit/Publish control without the manage code, and the backend
       * answers 403 regardless.
       */
      {
        path: ROUTES.taxConfiguration,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.TAX_CONFIG_READ]}>
            <LazyRoute>
              <TaxConfigurationPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
       * Feature 021 (021/design.md §6.1), replacing 016's `/moulds`. Guarded on the **read** code,
       * like every read/manage-split feature: `mould.manage` does not imply `mould.read`. The page
       * renders no input, Save or preview call without `mould.manage`, and the backend answers
       * 403 regardless. Neither code → no nav item and the standard 403 here. `/moulds` itself is
       * no longer declared and falls through to NotFound below.
       */
      {
        path: ROUTES.mouldSetting,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.MOULD_READ]}>
            <LazyRoute>
              <PlantMouldSettingPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
       * Feature 016 (016/design.md §7.1). Guarded on the read code; the page renders no Create,
       * Edit or Confirm control without `manufacturing_plan.manage`.
       */
      {
        path: ROUTES.manufacturingPlans,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.MANUFACTURING_PLAN_READ]}>
            <LazyRoute>
              <ManufacturingPlansPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
       * Feature 005 (005/design.md §6.1). **One route, drawers over it** — the composer
       * (`?record=new`), the run document (`?record=<id>`) and the reversal sheet (`&reverse=1`)
       * are all query parameters over this path, and `nav-items.tsx` gains nothing: the two lists
       * reach each other through the `Plans · Production Runs` segmented control (OQ-11).
       *
       * Guarded on the read code. `production_run.create` does **not** imply it: a holder of
       * `create` alone can post a run and cannot list runs (§5.1), so that session gets this
       * screen's 403 panel — which is the designed answer, not a gap.
       *
       * Guarded on `PERMISSIONS.PRODUCTION_RUN_READ`.
       */
      {
        path: ROUTES.productionRuns,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.PRODUCTION_RUN_READ]}>
            <LazyRoute>
              <ProductionRunsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
       * Feature 011 (011/design.md §8.2). Either grant opens the page: `payment.read` shows the
       * register, `payment.create` alone shows only the record-a-payment state (design §6 RBAC
       * note, QA case 48). The page renders no Record control without `payment.create`, and the
       * backend answers 403 regardless.
       */
      {
        path: ROUTES.payments,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.PAYMENT_READ, PERMISSIONS.PAYMENT_CREATE]}>
            <LazyRoute>
              <PaymentsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.accountHeads,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.ACCOUNT_HEAD_READ]}>
            <LazyRoute>
              <AccountHeadsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: ROUTES.paymentMethods,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.PAYMENT_METHOD_READ]}>
            <LazyRoute>
              <PaymentMethodsPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      /*
       * Feature 017 (017/design.md §7.2 screen 3). Finance workspace.
       * Guarded on `goods_receipt.read` — same grant as the receipts list; the
       * register is a read-only view over a subset of the same data. No new
       * permission code (§5.1, §8.1).
       */
      {
        path: ROUTES.tcsRegister,
        element: (
          <RequirePermission anyOf={[PERMISSIONS.GOODS_RECEIPT_READ]}>
            <LazyRoute>
              <TcsRegisterPage />
            </LazyRoute>
          </RequirePermission>
        ),
      },
      {
        path: '*',
        element: (
          <LazyRoute>
            <NotFoundPage />
          </LazyRoute>
        ),
      },
    ],
  },
]);
